import React, { useState, useEffect, useRef } from "react";
import { Col, Container, Row, Table, Card, Form } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";
import Header from "../../Component/Header";
import "./style.css";
import downArrowIcon from "../../Asset/Img/download-arrow-icon.svg";
import calendarIcon from "../../Asset/Img/calendar-icon.svg";
import SearchableDropdown from "../../Component/Dropdown";
import PortalDropdown from "../../Component/PortalDropdown";
import FullScreenLoader from "../../Component/Loader";
import {
  getEquipments,
  getCustomers,
  getRegions,
  savePriceBook,
  getPriceBookById,
  updatePriceBook,
  getDropdownValue,
  validateFile,
} from "../../Services/priceBook";

// --- Category normalization -------------------------------------------------
// Collapses variants like "3rd Party", "3RD PARTY", "3rd party rental",
// "Equipment", "Equipments", "Labor", "Labour" into the canonical names
// used by STATIC_CATEGORIES so items never silently disappear.
const STATIC_CATEGORIES = ["Equipments", "Services", "3RD Party", "Labour"];

const normalizeCategory = (raw) => {
  const value = String(raw || "").trim().toLowerCase();

  if (!value) return "Equipments";

  if (value.includes("3rd") || value.includes("third")) return "3RD Party";
  if (value.includes("equip")) return "Equipments";
  if (value.includes("service")) return "Services";
  if (value.includes("labour") || value.includes("labor")) return "Labour";

  // Unknown category: return the original trimmed string so it can still
  // be preserved (we will add it to the final category list below).
  return String(raw).trim();
};

function Home() {
  let navigate = useNavigate();
  const { pricebook_id } = useParams();

  // --- State ---
  const [showLoader, setShowLoader] = useState(false);
  const [equipmentList, setEquipmentList] = useState({});
  const [customerOptions, setCustomerOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [dropDownValue, setDropDownValue] = useState({});
  const [tableData, setTableData] = useState([]);
  const [expandedRows, setExpandedRows] = useState([]);
  const [priceBookData, setPriceBookData] = useState(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  // --- XLS upload wizard state ---
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadStep, setUploadStep] = useState(1);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadResponse, setUploadResponse] = useState(null);
  const [uploadValidationError, setUploadValidationError] = useState("");
  const [uploadHasExtractedData, setUploadHasExtractedData] = useState(false);
  const [uploadTemplatePaths, setUploadTemplatePaths] = useState([]);
  const [reviewGroups, setReviewGroups] = useState([]);
  const [reviewExpandedGroups, setReviewExpandedGroups] = useState({ Equipments: true });
  const [uploadSubmitting, setUploadSubmitting] = useState(false);

  // --- Separate equipment state for the upload wizard (uses extracted country) ---
  const [uploadEquipmentList, setUploadEquipmentList] = useState({});
  const [loadingUploadEquipments, setLoadingUploadEquipments] = useState(false);

  // --- Loading states ---
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingEquipments, setLoadingEquipments] = useState(false);

  // --- Refs ---
  const isEditMode = useRef(!!pricebook_id);
  const isInitialLoad = useRef(true);
  const isManuallyChangingCountry = useRef(false);
  const originalPriceBookData = useRef(null);
  const currentCountryRef = useRef(null);
  const isCustomerSet = useRef(false);
  const customerSetAttempted = useRef(false);
  const customerSetTimeoutRef = useRef(null);
  const isSettingCountryFromEdit = useRef(false);
  const fileInputRef = useRef(null);

  const startDateInputRef = useRef(null);
  const endDateInputRef = useRef(null);

  // --- Helpers ---
  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '0';
    const hasDecimals = num % 1 !== 0;
    return hasDecimals ? num.toFixed(2) : num.toString();
  };

  const getFormattedSum = (data, property) => {
    let total = 0;
    data.forEach((category) => {
      category.details.forEach((item) => {
        const qty = toNumber(item.Quantity);
        const value = toNumber(item[property]);
        total += qty * value;
      });
    });
    return formatCurrency(total);
  };

  const getFormattedSubSum = (array, property) => {
    let total = 0;
    array.forEach((item) => {
      const qty = toNumber(item.Quantity);
      const value = toNumber(item[property]);
      total += qty * value;
    });
    return formatCurrency(total);
  };

  useEffect(() => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (end <= start) setEndDate("");
    }
  }, [startDate]);

  // --- Effects ---
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        if (isMounted) {
          if (pricebook_id) setShowLoader(true);

          const [
            dropdownData,
            regionsData,
            equipmentsData,
            priceBookDataResult,
          ] = await Promise.all([
            fetchDropdownValues(),
            fetchRegions(),
            fetchEquipments(),
            pricebook_id ? fetchPriceBookById() : Promise.resolve(null),
          ]);

          if (dropdownData) setDropDownValue(dropdownData);
          if (regionsData) setRegionOptions(regionsData);

          if (equipmentsData && Object.keys(equipmentsData).length > 0) {
            setEquipmentList(equipmentsData);

            if (pricebook_id && priceBookDataResult) {
              // Seed with empty categories; populateTableData will fill them.
              const newTableData = STATIC_CATEGORIES.map((category, index) => ({
                id: index + 1,
                nameField: category,
                details: []
              }));
              setTableData(newTableData);
              if (priceBookDataResult?.items) {
                populateTableData(priceBookDataResult.items);
              }
            } else {
              initializeTableData();
            }
          }

          if (pricebook_id && priceBookDataResult) {
            originalPriceBookData.current = priceBookDataResult;
            setPriceBookData(priceBookDataResult);
            populateFilters(priceBookDataResult, dropdownData);
          }

          setIsDataLoaded(true);
          setShowLoader(false);
          isInitialLoad.current = false;
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching data error:", error);
          setShowLoader(false);
          isInitialLoad.current = false;
        }
      }
    };

    fetchData();
    return () => {
      isMounted = false;
      if (customerSetTimeoutRef.current) clearTimeout(customerSetTimeoutRef.current);
    };
  }, [pricebook_id]);

  useEffect(() => {
    const loadEquipments = async () => {
      const countryName = selectedCountry?.label;
      if (isInitialLoad.current) return;

      if (countryName) {
        const equipmentsData = await fetchEquipments(countryName);
        if (equipmentsData && Object.keys(equipmentsData).length > 0) {
          setEquipmentList(equipmentsData);
        } else {
          setEquipmentList({});
        }
      } else {
        setEquipmentList({});
      }
    };
    loadEquipments();
  }, [selectedCountry]);

  const initializeTableData = () => {
    const newTableData = STATIC_CATEGORIES.map((category, index) => ({
      id: index + 1,
      nameField: category,
      details: [
        {
          OrderDetailsId: null,
          BillableResource_ID: null,
          ProductId: "",
          ProductName: "",
          Quantity: null,
          OilBasedMud: null,
          WaterBasedMud: null,
          StandBy: null,
          RigMove: null,
          QuotedPrice: null,
          description: "",
          UnitPrice: 0,
          CreatedBy: localStorage?.userName || "",
          UpdatedBy: localStorage?.userName || ""
        },
      ],
    }));
    setTableData(newTableData);
  };

  // --- FIXED: normalize category name so "3rd Party" / "3RD Party" /
  //           "3RD PARTY" all map to the same STATIC_CATEGORIES entry.
  const populateTableData = (items) => {
    if (!items || items.length === 0) return;

    const groupedItems = {};

    items.forEach((group) => {
      const category = normalizeCategory(group?.nameField);

      if (!groupedItems[category]) groupedItems[category] = [];

      (group?.details || []).forEach((detail) => {
        const productId =
          detail?.ProductId ??
          detail?.BillableResource_ID ??
          detail?.productId ??
          null;

        const productName =
          detail?.ProductName ??
          detail?.productName ??
          detail?.name ??
          "";

        // Keep the row if it has either a product id or a product name.
        if (!productId && !productName) return;

        groupedItems[category].push({
          OrderDetailsId: detail?.OrderDetailsId ?? null,
          BillableResource_ID: productId ?? null,
          ProductId: productId ?? null,
          ProductName: productName,
          Quantity: detail?.quantity ?? detail?.Quantity ?? null,
          OilBasedMud: detail?.oilBasedMud ?? detail?.OilBasedMud ?? null,
          WaterBasedMud: detail?.waterBasedMud ?? detail?.WaterBasedMud ?? null,
          StandBy: detail?.standBy ?? detail?.StandBy ?? null,
          RigMove: detail?.rigMove ?? detail?.RigMove ?? null,
          QuotedPrice: detail?.quotedPrice ?? detail?.QuotedPrice ?? null,
          description: detail?.description || "",
          UnitPrice: detail?.UnitPrice ?? detail?.unitprice ?? 0,
          CreatedBy: detail?.CreatedBy || localStorage?.userName || "",
          UpdatedBy: detail?.UpdatedBy || localStorage?.userName || "",
        });
      });
    });

    setTableData((prevData) => {
      const existingDataMap = {};
      if (prevData.length > 0) {
        prevData.forEach((section) => {
          existingDataMap[section.nameField] = section.details;
        });
      }

      // Preserve any unknown categories returned by the API.
      const finalCategories = [...STATIC_CATEGORIES];
      Object.keys(groupedItems).forEach((key) => {
        if (!finalCategories.includes(key)) finalCategories.push(key);
      });

      return finalCategories.map((category, index) => {
        const normalized = normalizeCategory(category);

        let details = [];

        if (groupedItems[normalized]?.length > 0) {
          details = groupedItems[normalized];
        } else if (groupedItems[category]?.length > 0) {
          details = groupedItems[category];
        } else if (existingDataMap[category]?.length > 0) {
          details = existingDataMap[category];
        } else {
          details = [{
            OrderDetailsId: null,
            BillableResource_ID: null,
            ProductId: "",
            ProductName: "",
            Quantity: null,
            OilBasedMud: null,
            WaterBasedMud: null,
            StandBy: null,
            RigMove: null,
            QuotedPrice: null,
            description: "",
            UnitPrice: 0,
            CreatedBy: localStorage?.userName || "",
            UpdatedBy: localStorage?.userName || "",
          }];
        }

        return { id: index + 1, nameField: category, details };
      });
    });
  };

  useEffect(() => {
    const loadCustomers = async () => {
      const countryLabel = selectedCountry?.label;
      if (countryLabel) {
        currentCountryRef.current = countryLabel;
        await fetchCustomers(countryLabel);
        isManuallyChangingCountry.current = false;
      } else {
        setCustomerOptions([]);
        setSelectedCustomer(null);
        currentCountryRef.current = null;
        isCustomerSet.current = false;
        customerSetAttempted.current = false;
      }
    };
    loadCustomers();
  }, [selectedCountry]);

  useEffect(() => {
    if (!isEditMode.current || isInitialLoad.current) return;
    if (customerSetTimeoutRef.current) {
      clearTimeout(customerSetTimeoutRef.current);
      customerSetTimeoutRef.current = null;
    }
    if (isSettingCountryFromEdit.current) return;

    const orderDetails = originalPriceBookData.current;
    if (
      orderDetails?.Customer_ID &&
      customerOptions.length > 0 &&
      !isCustomerSet.current &&
      !customerSetAttempted.current
    ) {
      customerSetTimeoutRef.current = setTimeout(() => {
        const result = customerOptions.find(
          customer => customer.CustomerId === orderDetails?.Customer_ID
        );
        if (result) {
          setSelectedCustomer(result);
          isCustomerSet.current = true;
          customerSetAttempted.current = true;
        } else {
          customerSetAttempted.current = true;
        }
        customerSetTimeoutRef.current = null;
      }, 50);
    }
  }, [customerOptions, selectedCountry]);

  // --- API calls ---
  const fetchPriceBookById = async () => {
    try {
      const data = await getPriceBookById(localStorage?.access_token, pricebook_id);
      if (data?.data?.status_code === 200) return data?.data?.order_details;
      return null;
    } catch (error) {
      console.error("Error fetching price book:", error);
      return null;
    }
  };

  const populateFilters = (orderDetails, dropdownData) => {
    if (!orderDetails) return;
    isSettingCountryFromEdit.current = true;

    if (orderDetails?.Country && dropdownData?.Country) {
      const countryOption = dropdownData.Country.find(
        (item) => item.label === orderDetails.Country || item.value === orderDetails.Country
      );
      if (countryOption) setSelectedCountry(countryOption);
    }

    if (orderDetails?.IndustryType && dropdownData?.Industry) {
      const industryOption = dropdownData.Industry.find(
        (item) => item.label === orderDetails.IndustryType || item.value === orderDetails.IndustryType
      );
      if (industryOption) setSelectedIndustry(industryOption);
    }

    if (orderDetails?.RegionName) {
      const region = regionOptions.find(
        (item) => item.label === orderDetails.RegionName || item.value === orderDetails.RegionName
      );
      if (region) setSelectedRegion(region);
      else setSelectedRegion({
        RegionId: orderDetails.Region_ID || "",
        value: orderDetails.RegionName,
        label: orderDetails.RegionName,
      });
    }

    if (orderDetails?.StartDate) setStartDate(orderDetails.StartDate.split("T")[0]);
    if (orderDetails?.EndDate) setEndDate(orderDetails.EndDate.split("T")[0]);

    setTimeout(() => { isSettingCountryFromEdit.current = false; }, 200);
  };

  const fetchCustomers = async (country) => {
    if (!country) return;
    setLoadingCustomers(true);
    try {
      const data = await getCustomers(localStorage?.access_token, country);
      if (data?.data?.status_code === 200) {
        const customers = data.data?.customers || [];
        const uniqueCustomers = customers.filter((customer, index, self) =>
          index === self.findIndex((c) => c.value === customer.value)
        );
        if (currentCountryRef.current === country) setCustomerOptions(uniqueCustomers);
      } else {
        if (currentCountryRef.current === country) setCustomerOptions([]);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      if (currentCountryRef.current === country) setCustomerOptions([]);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const fetchRegions = async () => {
    setLoadingRegions(true);
    try {
      const data = await getRegions(localStorage?.access_token);
      if (data?.data && data?.data?.regions) return data.data?.regions;
      return [];
    } catch (error) {
      console.error("Error fetching regions:", error);
      return [];
    } finally {
      setLoadingRegions(false);
    }
  };

  const fetchEquipments = async (countryName = null) => {
    setLoadingEquipments(true);
    try {
      const country = countryName ?? (selectedCountry ? selectedCountry.label : "");
      const data = await getEquipments(localStorage?.access_token, country);

      if (data?.data?.status_code === 200) {
        const products = data.data?.products || {};
        const transformedProducts = {};
        for (const category in products) {
          if (products.hasOwnProperty(category)) {
            transformedProducts[category] = products[category].map(item => ({
              value: item.ProductId || item.value,
              label: item.label || item.ProductName,
              id: item.ProductId || item.value,
              UnitPrice: item.unitprice || 0,
              BillableResource_ID: item.ProductId || item.value,
              ProductId: item.ProductId || item.value,
              ProductName: item.label || item.ProductName,
              ...item
            }));
          }
        }
        return transformedProducts;
      }
      return {};
    } catch (error) {
      console.error("Error fetching equipments:", error);
      return {};
    } finally {
      setLoadingEquipments(false);
    }
  };

  // Fetch equipment specifically for the upload wizard using the extracted country.
  const fetchUploadEquipments = async (countryName) => {
    if (!countryName) {
      setUploadEquipmentList({});
      return {};
    }
    setLoadingUploadEquipments(true);
    try {
      const data = await getEquipments(localStorage?.access_token, countryName);
      if (data?.data?.status_code === 200) {
        const products = data.data?.products || {};
        const transformedProducts = {};
        for (const category in products) {
          if (products.hasOwnProperty(category)) {
            transformedProducts[category] = products[category].map(item => ({
              value: item.ProductId || item.value,
              label: item.label || item.ProductName,
              id: item.ProductId || item.value,
              UnitPrice: item.unitprice || 0,
              BillableResource_ID: item.ProductId || item.value,
              ProductId: item.ProductId || item.value,
              ProductName: item.label || item.ProductName,
              ...item
            }));
          }
        }
        setUploadEquipmentList(transformedProducts);
        return transformedProducts;
      }
      setUploadEquipmentList({});
      return {};
    } catch (error) {
      console.error("Error fetching upload equipments:", error);
      setUploadEquipmentList({});
      return {};
    } finally {
      setLoadingUploadEquipments(false);
    }
  };

  const fetchDropdownValues = async () => {
    setLoadingDropdowns(true);
    try {
      const data = await getDropdownValue(localStorage?.access_token);
      if (data?.data?.products?.result) {
        const raw = data.data.products.result;
        const labelMap = { Country: "country", Industry: "Industry" };
        const transformed = {};
        for (const key in raw) {
          if (raw.hasOwnProperty(key)) {
            const labelKey = labelMap[key];
            if (labelKey) {
              transformed[key] = raw[key].map((item) => ({
                value: item.ID, label: item[labelKey],
              }));
            } else {
              transformed[key] = raw[key].map((item) => ({
                value: item.ID, label: item[key] || item.ID,
              }));
            }
          }
        }
        return transformed;
      }
      return {};
    } catch (error) {
      console.error("Error fetching dropdown values:", error);
      return {};
    } finally {
      setLoadingDropdowns(false);
    }
  };

  // --- XLS upload wizard helpers ---
  const getValidationPayload = () => uploadResponse?.data || {};

  const getUploadData = () => {
    const payload = getValidationPayload();
    return (
      payload?.result?.extracted_data ||
      payload?.extracted_data ||
      payload?.data?.extracted_data ||
      payload?.result?.data?.extracted_data ||
      {}
    );
  };

  const getReviewCustomer = () => {
    const data = getUploadData();
    const customerId = data?.Customer_ID ?? data?.CustomerId;
    const option = customerOptions.find(
      (item) => String(item.CustomerId ?? item.value) === String(customerId)
    );
    return (
      data?.CustomerName || data?.Customer || data?.Customer_Name ||
      option?.label || selectedCustomer?.label || "-"
    );
  };

  const normalizeReviewGroups = (items) => {
    if (!Array.isArray(items)) return [];

    const grouped = items.some((item) => Array.isArray(item?.details))
      ? items
      : [{ nameField: "Equipments", details: items }];

    return grouped.map((group, groupIndex) => ({
      id: group?.id ?? `${group?.nameField || "group"}-${groupIndex}`,
      nameField: normalizeCategory(group?.nameField),
      details: (group?.details || []).map((detail, index) => ({
        id: detail?.id ?? `${groupIndex}-${index}-${Date.now()}`,
        OrderDetailsId: detail?.OrderDetailsId || null,
        BillableResource_ID:
          detail?.BillableResource_ID ?? detail?.ProductId ?? detail?.productId ?? null,
        ProductId: detail?.ProductId ?? detail?.productId ?? "",
        ProductName:
          detail?.ProductName || detail?.productName || detail?.name || detail?.label || "",
        ResourceGroup:
          detail?.ResourceGroup || detail?.["resource group no."] || detail?.resource_group_no || "",
        BcListedPrice:
          detail?.BcListedPrice ?? detail?.["bc listed price"] ?? detail?.bc_listed_price ?? null,
        Quantity: detail?.Quantity ?? detail?.quantity ?? 1,
        QuotedPrice:
          detail?.QuotedPrice ?? detail?.quotedPrice ?? detail?.quoted_price ?? null,
        StandBy: detail?.StandBy ?? detail?.standBy ?? detail?.stand_by ?? null,
        OilBasedMud: detail?.OilBasedMud ?? detail?.oilBasedMud ?? null,
        WaterBasedMud: detail?.WaterBasedMud ?? detail?.waterBasedMud ?? null,
        RigMove: detail?.RigMove ?? detail?.rigMove ?? null,
        UnitPrice:
          detail?.UnitPrice ?? detail?.unitprice ?? detail?.["bc listed price"] ?? 0,
        description: detail?.description || "",
      })),
    }));
  };

  const detectIndustryFromItems = () => {
    const rows = reviewGroups.flatMap((group) => group.details || []);
    if (rows.length === 0) return "";

    const hasValue = (val) =>
      val !== null && val !== undefined &&
      String(val).trim() !== "" && !isNaN(parseFloat(val));

    const isOFSData = rows.some(
      (row) =>
        hasValue(row?.OilBasedMud) ||
        hasValue(row?.WaterBasedMud) ||
        hasValue(row?.RigMove)
    );
    return isOFSData ? "OFS" : "ES";
  };

  const isReviewRowValid = (detail) => {
    const hasProduct = Boolean(
      detail?.ProductId || detail?.BillableResource_ID || detail?.ProductName
    );
    const hasQuantity =
      detail?.Quantity === null || detail?.Quantity === undefined ||
        String(detail.Quantity).trim() !== ""
        ? true : false;

    const importedIndustry = String(
      getUploadData()?.IndustryType || selectedIndustry?.label || ""
    ).trim().toLowerCase();

    const resolvedIndustry = importedIndustry || detectIndustryFromItems().toLowerCase();
    const isOFS = resolvedIndustry === "ofs";

    const price = isOFS
      ? (detail?.QuotedPrice ?? detail?.OilBasedMud ?? detail?.BcListedPrice)
      : (detail?.QuotedPrice ?? detail?.BcListedPrice);

    const hasPrice =
      price !== null && price !== undefined && String(price).trim() !== "";

    return hasProduct && hasQuantity && hasPrice;
  };

  const getReviewCounts = () => {
    const rows = reviewGroups.flatMap((group) => group.details || []);
    const invalid = rows.filter((row) => !isReviewRowValid(row)).length;
    return { total: rows.length, valid: Math.max(rows.length - invalid, 0), invalid };
  };

  const resetUploadWizard = () => {
    setShowUploadModal(false);
    setUploadStep(1);
    setUploadedFile(null);
    setUploadResponse(null);
    setUploadValidationError("");
    setUploadHasExtractedData(false);
    setUploadTemplatePaths([]);
    setReviewGroups([]);
    setReviewExpandedGroups({ Equipments: true });
    setUploadSubmitting(false);
    setUploadEquipmentList({});
    setLoadingUploadEquipments(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const openUploadWizard = () => {
    setUploadStep(1);
    setUploadedFile(null);
    setUploadResponse(null);
    setUploadValidationError("");
    setUploadHasExtractedData(false);
    setUploadTemplatePaths([]);
    setReviewGroups([]);
    setReviewExpandedGroups({ Equipments: true });
    setUploadEquipmentList({});
    setLoadingUploadEquipments(false);
    setShowUploadModal(true);
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFileValidation = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("uploaded_by", localStorage?.userName || "");
    formData.append("user_email", localStorage?.email || "");

    try {
      const response = await validateFile(localStorage?.access_token, formData);
      return { ok: true, response, payload: response?.data || {} };
    } catch (error) {
      return {
        ok: false,
        response: error?.response || null,
        payload: error?.response?.data || {},
        error,
      };
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validExtensions = [".xlsx", ".xls"];
    const fileExtension = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!validExtensions.includes(fileExtension)) {
      toast.error("Please upload a valid Excel file (.xlsx or .xls)");
      event.target.value = "";
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File size must be 15MB or less.");
      event.target.value = "";
      return;
    }

    setUploadingFile(true);
    setShowLoader(true);
    setUploadValidationError("");
    setUploadHasExtractedData(false);
    setReviewGroups([]);
    setUploadEquipmentList({});

    try {
      const validationResult = await handleFileValidation(file);
      const payload = validationResult.payload || {};

      const extractedData =
        payload?.result?.extracted_data ||
        payload?.extracted_data ||
        payload?.data?.extracted_data ||
        null;

      const hasExtractedData =
        !!extractedData && typeof extractedData === "object" &&
        Array.isArray(extractedData?.items);

      const isSuccess =
        validationResult.ok &&
        (payload?.success === true || payload?.status_code === 200);

      const isError =
        !isSuccess || payload?.success === false || Number(payload?.status_code) >= 400;

      setUploadedFile(file);
      setUploadResponse({ data: payload, httpResponse: validationResult.response });

      setUploadTemplatePaths(
        (() => {
          const raw =
            payload?.Template_path ?? payload?.template_path ??
            payload?.result?.Template_path ?? payload?.result?.template_path ?? null;
          if (!raw) return [];
          if (Array.isArray(raw)) return raw.map((item) =>
            typeof item === "string" ? item : item?.url || item?.path || item?.href
          ).filter(Boolean);
          if (typeof raw === "string") return [raw];
          if (typeof raw === "object") {
            const url = raw?.url || raw?.path || raw?.href;
            return url ? [url] : Object.values(raw).filter((value) => typeof value === "string" && value);
          }
          return [];
        })()
      );

      setUploadValidationError(isError ? payload?.message || "File validation failed." : "");
      setUploadHasExtractedData(hasExtractedData);

      if (hasExtractedData) {
        const groups = normalizeReviewGroups(extractedData.items);
        setReviewGroups(groups);
        setReviewExpandedGroups(
          groups.reduce((acc, group) => ({ ...acc, [group.nameField]: true }), {})
        );

        const extractedCountry =
          extractedData?.Country ||
          extractedData?.country ||
          payload?.result?.Country ||
          payload?.Country ||
          selectedCountry?.label ||
          null;

        if (extractedCountry) {
          await fetchUploadEquipments(extractedCountry);
        } else {
          setUploadEquipmentList({});
        }
      } else {
        setReviewGroups([]);
        setReviewExpandedGroups({ Equipments: true });
        setUploadEquipmentList({});
      }

      setUploadStep(2);

      if (isSuccess && hasExtractedData) {
        toast.success(payload?.message || "File validated successfully.");
      } else if (isError && hasExtractedData) {
        toast.error(payload?.message || "File validation completed with errors.");
      } else if (isError) {
        toast.error(payload?.message || "File validation failed.");
      }
    } catch (error) {
      console.error("Error validating uploaded file:", error);
      setUploadedFile(file);
      setUploadResponse({
        data: {
          success: false,
          status_code: error?.response?.status || 500,
          message: error?.response?.data?.message || "An error occurred while validating the file.",
          extracted_data: null,
        },
      });
      setUploadValidationError(
        error?.response?.data?.message || "An error occurred while validating the file."
      );
      const errorTemplatePath = error?.response?.data?.Template_path ?? error?.response?.data?.template_path;
      setUploadTemplatePaths(
        Array.isArray(errorTemplatePath)
          ? errorTemplatePath.filter(Boolean)
          : typeof errorTemplatePath === "string"
            ? [errorTemplatePath]
            : errorTemplatePath?.url || errorTemplatePath?.path || errorTemplatePath?.href
              ? [errorTemplatePath.url || errorTemplatePath.path || errorTemplatePath.href]
              : []
      );
      setUploadHasExtractedData(false);
      setReviewGroups([]);
      setUploadEquipmentList({});
      setUploadStep(2);
      toast.error(error?.response?.data?.message || "An error occurred while validating the file.");
    } finally {
      setUploadingFile(false);
      setShowLoader(false);
      event.target.value = "";
    }
  };

  const handleUploadContinue = () => {
    if (!uploadHasExtractedData || reviewGroups.length === 0) {
      toast.warning("Cannot continue because no extracted data is available.");
      return;
    }
    setUploadStep(3);
  };

  const handleReviewRowChange = (groupIndex, rowIndex, field, value) => {
    setReviewGroups((prev) =>
      prev.map((group, gi) =>
        gi !== groupIndex ? group : {
          ...group,
          details: group.details.map((detail, ri) =>
            ri !== rowIndex ? detail : {
              ...detail,
              [field]: value,
              ...(field === "ProductId" ? { BillableResource_ID: value } : {}),
            }
          ),
        }
      )
    );
  };

  const handleReviewProductChange = (groupIndex, rowIndex, product) => {
    const productId = product?.ProductId ?? product?.id ?? product?.value ?? "";
    const productName = product?.ProductName ?? product?.label ?? "";

    setReviewGroups((prev) =>
      prev.map((group, gi) =>
        gi !== groupIndex ? group : {
          ...group,
          details: group.details.map((detail, ri) =>
            ri !== rowIndex ? detail : {
              ...detail,
              ProductId: productId,
              BillableResource_ID: productId || null,
              ProductName: productName,
              UnitPrice: product?.UnitPrice ?? product?.unitprice ?? detail.UnitPrice ?? 0,
            }
          ),
        }
      )
    );
  };

  const handleReviewAddRow = (groupIndex) => {
    setReviewGroups((prev) =>
      prev.map((group, gi) =>
        gi !== groupIndex ? group : {
          ...group,
          details: [
            ...group.details,
            {
              id: `review-${Date.now()}-${groupIndex}`,
              OrderDetailsId: null,
              BillableResource_ID: null,
              ProductId: "",
              ProductName: "",
              ResourceGroup: "",
              BcListedPrice: null,
              Quantity: 1,
              QuotedPrice: null,
              StandBy: null,
              OilBasedMud: null,
              WaterBasedMud: null,
              RigMove: null,
              UnitPrice: 0,
              description: "",
            },
          ],
        }
      )
    );
  };

  const handleReviewRemoveRow = (groupIndex, rowIndex) => {
    setReviewGroups((prev) =>
      prev.map((group, gi) =>
        gi !== groupIndex ? group : {
          ...group,
          details: group.details.filter((_, ri) => ri !== rowIndex),
        }
      )
    );
  };

  const toggleReviewGroup = (groupName) => {
    setReviewExpandedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const handleUploadSubmit = async () => {
    const counts = getReviewCounts();
    if (counts.invalid > 0) {
      toast.warning(
        `Please fix the ${counts.invalid} row${counts.invalid === 1 ? "" : "s"} marked with missing information before submitting.`
      );
      return;
    }

    const data = getUploadData();
    const importedIndustryRaw = data?.IndustryType || selectedIndustry?.label || "";
    const resolvedIndustry =
      String(importedIndustryRaw).trim() || detectIndustryFromItems() || "ES";
    const importedIsOFS = String(resolvedIndustry).trim().toLowerCase() === "ofs";

    const items = reviewGroups.flatMap((group) =>
      (group.details || [])
        .filter((detail) => detail?.ProductId || detail?.BillableResource_ID)
        .map((detail) => ({
          OrderDetailsId: detail?.OrderDetailsId ? parseInt(detail.OrderDetailsId) : 0,
          BillableResource_ID: parseInt(detail.BillableResource_ID || detail.ProductId),
          ProductId: parseInt(detail.ProductId || detail.BillableResource_ID),
          ProductName: detail.ProductName || "",
          Quantity: parseInt(detail.Quantity) || 0,
          CreatedBy: detail.CreatedBy || localStorage?.userName || "",
          UpdatedBy: localStorage?.userName || "",
          ...(importedIsOFS ? {
            OilBasedMud: parseFloat(detail.OilBasedMud) || 0,
            WaterBasedMud: parseFloat(detail.WaterBasedMud) || 0,
            StandBy: detail.StandBy === null || detail.StandBy === undefined || detail.StandBy === "" ? null : String(detail.StandBy),
            RigMove: detail.RigMove === null || detail.RigMove === undefined || detail.RigMove === "" ? null : String(detail.RigMove),
          } : {
            StandBy: detail.StandBy === null || detail.StandBy === undefined || detail.StandBy === "" ? null : String(detail.StandBy),
            QuotedPrice: detail.QuotedPrice === null || detail.QuotedPrice === undefined || detail.QuotedPrice === "" ? null : String(detail.QuotedPrice),
          }),
        }))
    );

    const customerId = data?.Customer_ID ?? selectedCustomer?.CustomerId ?? selectedCustomer?.value;
    const regionId = data?.Region_ID ?? selectedRegion?.RegionId ?? selectedRegion?.value;

    if (!customerId || !regionId || items.length === 0) {
      toast.error("The uploaded workbook does not contain enough information to create the Price Book.");
      return;
    }

    setUploadSubmitting(true);
    try {
      const saveResponse = await savePriceBook(
        {
          OrderId: null,
          Customer_ID: parseInt(customerId),
          Region_ID: parseInt(regionId),
          StartDate: data?.StartDate?.split("T")[0] || startDate || new Date().toISOString().split("T")[0],
          EndDate: data?.EndDate?.split("T")[0] || endDate || null,
          CreatedByEmail: localStorage?.email || "",
          CreatedBy: localStorage?.userName || "",
          UpdatedBy: localStorage?.userName || "",
          IndustryType: resolvedIndustry,
          Country: data?.Country || selectedCountry?.label || "",
          items,
        },
        localStorage?.access_token
      );

      if (saveResponse?.data?.status_code === 200) {
        toast.success(saveResponse?.data?.message || "Price Book Template submitted successfully!");
        setShowUploadModal(false);
        const id = saveResponse?.data?.PriceBookOrder_ID;
        if (id) navigate(`/pricebook/${id}`);
      } else if (saveResponse?.data?.status_code === 409) {
        toast.warning(saveResponse?.data?.message || "Price Book already exists.");
      } else {
        toast.error(saveResponse?.data?.message || "Failed to submit Price Book Template.");
      }
    } catch (error) {
      console.error("Error submitting uploaded Price Book:", error);
      toast.error("An error occurred while submitting the Price Book Template.");
    } finally {
      setUploadSubmitting(false);
    }
  };

  const validateDates = () => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (end <= start) {
        toast.warning("Expiry date must be after start date.");
        return false;
      }
    }
    return true;
  };

  const openDatePicker = (inputRef) => {
    const input = inputRef?.current;
    if (!input) return;
    try {
      input.focus();
      if (typeof input.showPicker === "function") input.showPicker();
      else input.click();
    } catch (error) {
      input.click();
    }
  };

  const handleResetFilters = () => {
    setSelectedCountry(null);
    setSelectedIndustry(null);
    setSelectedCustomer(null);
    setCustomerOptions([]);
    setSelectedRegion(null);
    setStartDate("");
    setEndDate("");
    setPriceBookData(null);
    originalPriceBookData.current = null;
    currentCountryRef.current = null;
    isCustomerSet.current = false;
    customerSetAttempted.current = false;
    isSettingCountryFromEdit.current = false;
  };

  const validateRequiredFields = () => {
    const requiredFields = [
      { label: "Country", value: selectedCountry },
      { label: "Industry", value: selectedIndustry },
      { label: "Customer", value: selectedCustomer },
      { label: "Region", value: selectedRegion },
      { label: "Start Date", value: startDate },
    ];

    for (let field of requiredFields) {
      if (!field.value || (typeof field.value === "string" && field.value.trim() === "")) {
        toast.warning(`Please select a ${field.label}`);
        return false;
      }
    }
    return true;
  };

  const getCurrentIndustry = () => {
    const data = getUploadData();
    const explicit = String(
      data?.IndustryType ?? data?.Industry ?? selectedIndustry?.label ?? ""
    ).trim();
    if (explicit) return explicit.toUpperCase();
    return detectIndustryFromItems().toUpperCase();
  };

  const isOFSIndustry = () => getCurrentIndustry() === "OFS";

  const handleSave = async () => {
    if (!validateRequiredFields()) return;
    if (!validateDates()) return;

    const items = [];
    tableData.forEach((section) => {
      section.details.forEach((detail) => {
        if (detail.BillableResource_ID) {
          const item = {
            OrderDetailsId: null,
            BillableResource_ID: parseInt(detail.BillableResource_ID),
            ProductId: parseInt(detail.ProductId) || parseInt(detail.BillableResource_ID),
            ProductName: detail.ProductName || "",
            Quantity: parseInt(detail.Quantity) || 0,
            CreatedBy: localStorage?.userName || "",
            UpdatedBy: localStorage?.userName || ""
          };
          if (isOFSIndustry()) {
            item.OilBasedMud = parseFloat(detail.OilBasedMud) || 0;
            item.WaterBasedMud = parseFloat(detail.WaterBasedMud) || 0;
            item.StandBy = (detail.StandBy) || null;
            item.RigMove = (detail.RigMove) || null;
          } else {
            item.StandBy = (detail.StandBy) || null;
            item.QuotedPrice = (detail.QuotedPrice) || null;
          }
          items.push(item);
        }
      });
    });

    const payload = {
      OrderId: null,
      Customer_ID: parseInt(selectedCustomer.CustomerId || selectedCustomer.value),
      Region_ID: parseInt(selectedRegion.RegionId || selectedRegion.value),
      StartDate: startDate || null,
      EndDate: endDate || null,
      CreatedByEmail: localStorage?.email || "",
      CreatedBy: localStorage?.userName || "",
      UpdatedBy: localStorage?.userName || "",
      IndustryType: selectedIndustry?.label || "",
      Country: selectedCountry?.label || "",
      items,
    };

    if (items.length === 0) {
      toast.warning("Please add at least one item to the price book.");
      return;
    }

    setShowLoader(true);
    try {
      const data = await savePriceBook(payload, localStorage?.access_token);
      if (data?.data?.status_code === 200) {
        toast.success(data?.data?.message || "Price Book saved successfully!");
        setTimeout(() => { navigate(`/pricebook/${data?.data?.PriceBookOrder_ID}`); }, 3000);
      } else if (data?.data?.status_code === 409) {
        toast.warning(data?.data?.message || "Price Book already exists.");
      } else {
        toast.error(data?.data?.message || "Failed to save Price Book.");
      }
    } catch (error) {
      console.error("Error saving price book:", error);
      toast.error("An error occurred while saving the Price Book.");
    } finally {
      setShowLoader(false);
    }
  };

  const handleUpdate = async () => {
    if (!validateRequiredFields()) return;
    if (!validateDates()) return;

    const items = [];
    tableData.forEach((section) => {
      section.details.forEach((detail) => {
        if (detail.BillableResource_ID) {
          const item = {
            OrderDetailsId: detail.OrderDetailsId ? parseInt(detail.OrderDetailsId) : 0,
            BillableResource_ID: parseInt(detail.BillableResource_ID),
            ProductId: parseInt(detail.ProductId) || parseInt(detail.BillableResource_ID),
            ProductName: detail.ProductName || "",
            Quantity: parseInt(detail.Quantity) || null,
            CreatedBy: detail.CreatedBy || localStorage?.userName || "",
            UpdatedBy: localStorage?.userName || ""
          };
          if (isOFSIndustry()) {
            item.OilBasedMud = parseFloat(detail.OilBasedMud) || 0;
            item.WaterBasedMud = parseFloat(detail.WaterBasedMud) || 0;
            item.StandBy = String(detail.StandBy) || null;
            item.RigMove = String(detail.RigMove) || null;
          } else {
            item.StandBy = String(detail.StandBy) || null;
            item.QuotedPrice = String(detail.QuotedPrice) || null;
          }
          items.push(item);
        }
      });
    });

    const payload = {
      OrderId: parseInt(pricebook_id),
      Customer_ID: parseInt(selectedCustomer.CustomerId || selectedCustomer.value),
      Region_ID: parseInt(selectedRegion.RegionId || selectedRegion.value),
      StartDate: startDate || "",
      EndDate: endDate || "",
      CreatedByEmail: localStorage?.email || "",
      CreatedBy: localStorage?.userName || "",
      UpdatedBy: localStorage?.userName || "",
      IndustryType: selectedIndustry?.label || "",
      Country: selectedCountry?.label || "",
      items,
    };

    if (items.length === 0) {
      toast.warning("Please add at least one item to the price book.");
      return;
    }

    setShowLoader(true);
    try {
      const data = await updatePriceBook(payload, localStorage?.access_token);
      if (data?.data?.status_code === 200) {
        toast.success(data?.data?.message || "Price Book updated successfully!");
      } else if (data?.data?.status_code === 409) {
        toast.warning(data?.data?.message || "Price Book already exists.");
      } else {
        toast.error(data?.data?.message || "Failed to update Price Book.");
      }
    } catch (error) {
      console.error("Error updating price book:", error);
      toast.error("An error occurred while updating the Price Book.");
    } finally {
      setShowLoader(false);
    }
  };

  const toNumber = (val) => {
    const num = parseFloat(val);
    return isNaN(num) ? 0 : num;
  };

  const calSum = (data, property) => {
    let total = 0;
    data.forEach((category) => {
      category.details.forEach((item) => {
        total += toNumber(item.Quantity) * toNumber(item[property]);
      });
    });
    return total;
  };

  const handleFilterSelect = (value, filter) => {
    if (filter === "customer") setSelectedCustomer(value);
    else if (filter === "region") setSelectedRegion(value);
    else if (filter === "country") {
      isManuallyChangingCountry.current = true;
      isCustomerSet.current = false;
      customerSetAttempted.current = false;
      setSelectedCustomer(null);
      setCustomerOptions([]);
      setSelectedCountry(value);
    } else if (filter === "industry") setSelectedIndustry(value);
  };

  const handleInputSelect = (value, dataIndex, index, name) => {
    if (!value) {
      setTableData((prev) =>
        prev.map((section, i) =>
          i !== dataIndex ? section : {
            ...section,
            details: section.details.map((detail, j) =>
              j !== index ? detail : {
                ...detail,
                ProductName: "",
                ProductId: "",
                description: "",
                BillableResource_ID: null,
                Quantity: null,
                UnitPrice: 0,
              }
            ),
          }
        )
      );
      return;
    }

    setTableData((prev) =>
      prev.map((section, i) =>
        i !== dataIndex ? section : {
          ...section,
          details: section.details.map((detail, j) =>
            j !== index ? detail : {
              ...detail,
              ProductName: value.label,
              ProductId: value.id || value.value || value.ProductId,
              description: value.description || "",
              BillableResource_ID: value.id || value.value || value.ProductId,
              Quantity: 1,
              UnitPrice: value.UnitPrice || value.unitprice || 0,
            }
          ),
        }
      )
    );
  };

  const handleDateChange = (e, type) => {
    if (type === "start") setStartDate(e.target.value);
    else setEndDate(e.target.value);
  };

  const handleInputChange = (event, dataIndex, index) => {
    const { name, value } = event.target;
    if (value < 0) return;

    setTableData((prev) =>
      prev.map((section, i) =>
        i !== dataIndex ? section : {
          ...section,
          details: section.details.map((detail, j) =>
            j !== index ? detail : { ...detail, [name]: value }
          ),
        }
      )
    );
  };

  const handleAddRow = (index) => {
    const obj = {
      id: (Date.now() % 100000).toString().padStart(5, "0"),
      OrderDetailsId: null,
      BillableResource_ID: null,
      ProductId: "",
      ProductName: "",
      Quantity: null,
      OilBasedMud: null,
      WaterBasedMud: null,
      StandBy: null,
      RigMove: null,
      QuotedPrice: null,
      description: "",
      UnitPrice: 0,
      CreatedBy: localStorage?.userName || "",
      UpdatedBy: localStorage?.userName || ""
    };
    setTableData((prev) =>
      prev.map((section, i) =>
        i !== index ? section : { ...section, details: [...section.details, obj] }
      )
    );
  };

  const handleRemoveRow = (dataIndex, rowIndex) => {
    setTableData((prev) =>
      prev.map((section, i) =>
        i === dataIndex ? {
          ...section,
          details: section.details.filter((_, j) => j !== rowIndex),
        } : section
      )
    );
  };

  const toggleRowWeb = (id) => {
    setExpandedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const toggleAllRows = () => {
    if (expandedRows.length === tableData.length) setExpandedRows([]);
    else setExpandedRows(tableData.map((_, i) => i));
  };

  // Main page equipment options (uses the filter-driven equipmentList)
  const getEquipmentsOption = (name) => {
    if (Object.keys(equipmentList).length === 0) return [];
    if (equipmentList[name]) return equipmentList[name];

    const normalized = normalizeCategory(name);
    if (equipmentList[normalized]) return equipmentList[normalized];

    const lowerName = name.toLowerCase();
    for (const key in equipmentList) {
      if (key.toLowerCase() === lowerName) return equipmentList[key];
    }
    return [];
  };

  // Upload wizard equipment options (uses the country from the extracted data)
  const getUploadEquipmentsOption = (name) => {
    if (Object.keys(uploadEquipmentList).length === 0) return [];
    if (uploadEquipmentList[name]) return uploadEquipmentList[name];

    const normalized = normalizeCategory(name);
    if (uploadEquipmentList[normalized]) return uploadEquipmentList[normalized];

    const lowerName = name.toLowerCase();
    for (const key in uploadEquipmentList) {
      if (key.toLowerCase() === lowerName) return uploadEquipmentList[key];
    }
    return [];
  };

  if (loadingEquipments && tableData.length === 0) {
    return <FullScreenLoader />;
  }

  return (
    <div className="wrapper">
      {showLoader && <FullScreenLoader />}
      <ToastContainer position="top-right" autoClose={3000} />
      <Header />

      <div className="container-layout py-4">
        <Container fluid>
          <Row className="mb-4 align-items-center">
            <Col xs={12} md={6}>
              <h2 className="main-title-h mb-0">Price Book</h2>
              <p className="text-muted small">Manage your pricing and product catalog</p>
            </Col>

            <Col xs={12} md={6} className="text-md-end">
              <div className="d-inline-block me-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                  id="file-upload-input"
                />
                {!isEditMode?.current && (
                  <button
                    className="btn btn-upload-xls btn-sm me-2"
                    onClick={openUploadWizard}
                    disabled={uploadingFile}
                    style={{
                      background: uploadingFile ? '#cccccc' : 'linear-gradient(135deg, #082E56 0%, #1a4a7a 100%)',
                      color: '#ffffff',
                      border: 'none',
                      padding: '6px 16px',
                      borderRadius: '4px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      cursor: uploadingFile ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {uploadingFile ? '⏳ Uploading...' : '📤 Upload XLS'}
                  </button>
                )}
              </div>

              <button
                className="btn btn-expand-all btn-sm"
                onClick={toggleAllRows}
                style={{
                  background: 'linear-gradient(135deg, #082E56 0%, #1a4a7a 100%)',
                  color: '#ffffff',
                  border: 'none',
                  padding: '6px 16px',
                  borderRadius: '4px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                {expandedRows.length === tableData.length ? 'Collapse All' : 'Expand All'}
              </button>
            </Col>
          </Row>

          <Card className="shadow-sm mb-4" style={{ position: "relative" }}>
            {!isEditMode?.current && (
              <button
                className="btn btn-link p-0 border-0"
                onClick={handleResetFilters}
                title="Reset all filters"
                style={{
                  position: "absolute",
                  top: "10px", right: "12px",
                  fontSize: "18px", lineHeight: 1, color: "#8c8c8c",
                  zIndex: 5, textDecoration: "none",
                  opacity: 0.5, background: "transparent",
                  padding: "4px 6px", borderRadius: "4px",
                }}
              >
                ↺
              </button>
            )}
            <Card.Body>
              <Row>
                <Col xs={12} md={4} lg={2}>
                  <Form.Group className="mb-3">
                    <Form.Label className="required-field">Country <span className="text-danger">*</span></Form.Label>
                    <div className="position-relative">
                      <SearchableDropdown
                        disabled={isEditMode?.current}
                        options={dropDownValue.Country || []}
                        onSelect={(e) => handleFilterSelect(e, "country")}
                        placeholder="Select..."
                        selectedValue={selectedCountry?.label || ""}
                        isSearchable={true}
                        isClearable={true}
                        loading={loadingDropdowns}
                      />
                      <span className="icon-field-wt"><img src={downArrowIcon} alt="icon" /></span>
                    </div>
                  </Form.Group>
                </Col>

                <Col xs={12} md={4} lg={2}>
                  <Form.Group className="mb-3">
                    <Form.Label className="required-field">Industry <span className="text-danger">*</span></Form.Label>
                    <div className="position-relative">
                      <SearchableDropdown
                        disabled={isEditMode?.current}
                        options={dropDownValue.Industry || []}
                        onSelect={(e) => handleFilterSelect(e, "industry")}
                        placeholder="Select..."
                        selectedValue={selectedIndustry?.label || ""}
                        isSearchable={true}
                        isClearable={true}
                        loading={loadingDropdowns}
                      />
                      <span className="icon-field-wt"><img src={downArrowIcon} alt="icon" /></span>
                    </div>
                  </Form.Group>
                </Col>

                <Col xs={12} md={4} lg={2}>
                  <Form.Group className="mb-3">
                    <Form.Label className="required-field">Customer <span className="text-danger">*</span></Form.Label>
                    <div className="position-relative">
                      <SearchableDropdown
                        disabled={isEditMode?.current ? true : !selectedCountry}
                        key={`customer-dropdown-${selectedCountry?.value || 'no-country'}`}
                        options={customerOptions}
                        onSelect={(e) => handleFilterSelect(e, "customer")}
                        placeholder={!selectedCountry ? "Select country first" : "Select..."}
                        selectedValue={selectedCustomer?.label || ""}
                        isSearchable={true}
                        isClearable={true}
                        loading={loadingCustomers}
                      />
                      <span className="icon-field-wt"><img src={downArrowIcon} alt="icon" /></span>
                    </div>
                  </Form.Group>
                </Col>

                <Col xs={12} md={4} lg={2}>
                  <Form.Group className="mb-3">
                    <Form.Label className="required-field">Region <span className="text-danger">*</span></Form.Label>
                    <div className="position-relative">
                      <SearchableDropdown
                        disabled={isEditMode?.current}
                        options={regionOptions}
                        onSelect={(e) => handleFilterSelect(e, "region")}
                        placeholder="Select..."
                        selectedValue={selectedRegion?.label || ""}
                        isSearchable={true}
                        isClearable={true}
                        loading={loadingRegions}
                      />
                      <span className="icon-field-wt"><img src={downArrowIcon} alt="icon" /></span>
                    </div>
                  </Form.Group>
                </Col>

                <Col xs={12} md={4} lg={2}>
                  <Form.Group className="mb-3">
                    <Form.Label className="required-field">Start Date <span className="text-danger">*</span></Form.Label>
                    <div className="position-relative">
                      <input
                        disabled={isEditMode?.current}
                        ref={startDateInputRef}
                        value={startDate}
                        onChange={(e) => handleDateChange(e, "start")}
                        type="date"
                        className="form-control control-field-txt"
                        max={endDate || undefined}
                        style={{ paddingRight: "40px", cursor: "pointer" }}
                        onClick={() => openDatePicker(startDateInputRef)}
                      />
                      <span className="icon-field-wt" onClick={() => openDatePicker(startDateInputRef)}>
                        <img src={calendarIcon} alt="calendar" style={{ pointerEvents: "none" }} />
                      </span>
                    </div>
                  </Form.Group>
                </Col>

                <Col xs={12} md={4} lg={2}>
                  <Form.Group className="mb-3">
                    <Form.Label>Expiry Date</Form.Label>
                    <div className="position-relative">
                      <input
                        disabled={isEditMode?.current}
                        ref={endDateInputRef}
                        value={endDate}
                        onChange={(e) => handleDateChange(e, "end")}
                        type="date"
                        className="form-control control-field-txt"
                        min={startDate || undefined}
                        style={{ paddingRight: "40px", cursor: "pointer" }}
                        onClick={() => openDatePicker(endDateInputRef)}
                      />
                      <span className="icon-field-wt" onClick={() => openDatePicker(endDateInputRef)}>
                        <img src={calendarIcon} alt="calendar" style={{ pointerEvents: "none" }} />
                      </span>
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="shadow-sm">
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table responsive className="table-bookmark mb-0">
                  <tbody className="tbody-content-wp">
                    {tableData.map((item, i) => (
                      <React.Fragment key={item.id}>
                        <tr>
                          <td colSpan={isOFSIndustry() ? 6 : 5}>
                            <div className="table-button-mts">
                              <button type="button" className="btn btn-head-col" onClick={() => toggleRowWeb(i)}>
                                {item.nameField}
                              </button>
                            </div>
                          </td>
                        </tr>

                        {expandedRows.includes(i) && (
                          <tr>
                            <td colSpan={isOFSIndustry() ? 6 : 5} className="itms-tdwap p-3">
                              <Table className="sub-table-bookmark mb-0">
                                <thead className="thead-content-wp">
                                  <tr>
                                    <th>Equipment Name</th>
                                    <th>{ }</th>
                                    <th>Qty</th>
                                    {isOFSIndustry() ? (
                                      <>
                                        <th>Oil Based Mud</th>
                                        <th>Water Based Mud</th>
                                        <th>Stand By</th>
                                        <th>Rig Move</th>
                                      </>
                                    ) : (
                                      <>
                                        <th>Quoted Price</th>
                                        <th>Stand By</th>
                                      </>
                                    )}
                                    {!isEditMode?.current && <th>Action</th>}
                                  </tr>
                                </thead>

                                <tbody className="tbody-content-wp">
                                  {item.details.map((detail, index) => (
                                    <tr key={index}>
                                      <td>
                                        <div className="dropdown-equipment-old">
                                          <SearchableDropdown
                                            options={getEquipmentsOption(item.nameField)}
                                            onSelect={(e) => handleInputSelect(e, i, index, item.nameField)}
                                            placeholder="Select..."
                                            selectedValue={detail.ProductName}
                                            isSearchable={true}
                                            isClearable={true}
                                            loading={loadingEquipments}
                                          />
                                        </div>
                                      </td>

                                      <td>{/* {detail.description} */}</td>

                                      <td>
                                        <input
                                          min="1"
                                          onChange={(e) => handleInputChange(e, i, index)}
                                          type="number"
                                          name="Quantity"
                                          value={detail?.Quantity || ""}
                                          className="form-control tbl-input-control"
                                          style={{ width: "100px" }}
                                        />
                                      </td>

                                      {isOFSIndustry() ? (
                                        <>
                                          <td>
                                            <div className="price-flx-row">
                                              <div className="input-area-tbl">
                                                <div className="block-field-content">
                                                  <input type="number" min="0" className="form-control tbl-input-control"
                                                    onChange={(e) => handleInputChange(e, i, index)}
                                                    name="OilBasedMud" value={detail?.OilBasedMud || ""} style={{ width: "100px" }} />
                                                  <span className="txt-currency-vm">$</span>
                                                </div>
                                              </div>
                                              <span className="price-tag-mts ms-2">${formatCurrency(detail.UnitPrice || 0)}</span>
                                            </div>
                                          </td>
                                          <td>
                                            <div className="price-flx-row">
                                              <div className="input-area-tbl">
                                                <div className="block-field-content">
                                                  <input type="number" min="0" className="form-control tbl-input-control"
                                                    onChange={(e) => handleInputChange(e, i, index)}
                                                    name="WaterBasedMud" value={detail?.WaterBasedMud || ""} style={{ width: "100px" }} />
                                                  <span className="txt-currency-vm">$</span>
                                                </div>
                                              </div>
                                              <span className="price-tag-mts ms-2">${formatCurrency(detail.UnitPrice || 0)}</span>
                                            </div>
                                          </td>
                                          <td>
                                            <div className="price-flx-row">
                                              <div className="input-area-tbl">
                                                <div className="block-field-content">
                                                  <input type="number" min="0" className="form-control tbl-input-control"
                                                    onChange={(e) => handleInputChange(e, i, index)}
                                                    name="StandBy" value={detail?.StandBy || ""} style={{ width: "100px" }} />
                                                  <span className="txt-currency-vm">$</span>
                                                </div>
                                              </div>
                                              <span className="price-tag-mts ms-2">${formatCurrency(detail.UnitPrice || 0)}</span>
                                            </div>
                                          </td>
                                          <td>
                                            <div className="price-flx-row">
                                              <div className="input-area-tbl">
                                                <div className="block-field-content">
                                                  <input type="number" min="0" className="form-control tbl-input-control"
                                                    onChange={(e) => handleInputChange(e, i, index)}
                                                    name="RigMove" value={detail?.RigMove || ""} style={{ width: "100px" }} />
                                                  <span className="txt-currency-vm">$</span>
                                                </div>
                                              </div>
                                              <span className="price-tag-mts ms-2">${formatCurrency(detail.UnitPrice || 0)}</span>
                                            </div>
                                          </td>
                                        </>
                                      ) : (
                                        <>
                                          <td>
                                            <div className="price-flx-row">
                                              <div className="input-area-tbl">
                                                <div className="block-field-content">
                                                  <input type="number" min="0" className="form-control tbl-input-control"
                                                    onChange={(e) => handleInputChange(e, i, index)}
                                                    name="QuotedPrice" value={detail?.QuotedPrice || ""} style={{ width: "100px" }} />
                                                  <span className="txt-currency-vm">$</span>
                                                </div>
                                              </div>
                                              <span className="price-tag-mts ms-2">${formatCurrency(detail.UnitPrice || 0)}</span>
                                            </div>
                                          </td>
                                          <td>
                                            <div className="price-flx-row">
                                              <div className="input-area-tbl">
                                                <div className="block-field-content">
                                                  <input type="number" min="0" className="form-control tbl-input-control"
                                                    onChange={(e) => handleInputChange(e, i, index)}
                                                    name="StandBy" value={detail?.StandBy || ""} style={{ width: "100px" }} />
                                                  <span className="txt-currency-vm">$</span>
                                                </div>
                                              </div>
                                              <span className="price-tag-mts ms-2">${formatCurrency(detail.UnitPrice || 0)}</span>
                                            </div>
                                          </td>
                                        </>
                                      )}

                                      <td>
                                        {index > 0 && !isEditMode?.current && (
                                          <button type="button" className="btn btn-sm btn-outline-danger"
                                            onClick={() => handleRemoveRow(i, index)} title="Remove this item">✕</button>
                                        )}
                                      </td>
                                    </tr>
                                  ))}

                                  <tr>
                                    <td colSpan={isOFSIndustry() ? 8 : 7}>
                                      <div className="text-end">
                                        <button type="button" className="btn btn-add-list" onClick={() => handleAddRow(i)}>
                                          + Add Row
                                        </button>
                                      </div>
                                    </td>
                                  </tr>

                                  <tr>
                                    <td colSpan={3}>
                                      <span className="total-charge-ctg">Total ({item.nameField})</span>
                                    </td>
                                    {isOFSIndustry() ? (
                                      <>
                                        <td><span className="price-tbl-txt">${getFormattedSubSum(item.details, "OilBasedMud")}</span></td>
                                        <td><span className="price-tbl-txt">${getFormattedSubSum(item.details, "WaterBasedMud")}</span></td>
                                        <td><span className="price-tbl-txt">${getFormattedSubSum(item.details, "StandBy")}</span></td>
                                        <td><span className="price-tbl-txt">${getFormattedSubSum(item.details, "RigMove")}</span></td>
                                      </>
                                    ) : (
                                      <>
                                        <td><span className="price-tbl-txt">${getFormattedSubSum(item.details, "QuotedPrice")}</span></td>
                                        <td><span className="price-tbl-txt">${getFormattedSubSum(item.details, "StandBy")}</span></td>
                                      </>
                                    )}
                                    <td></td>
                                  </tr>
                                </tbody>
                              </Table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}

                    {tableData.length > 0 && (
                      <tr className="grand-total-wts-mtw">
                        <td className="grand-total-yt"><span className="total-charge-ctg">Total</span></td>
                        {isOFSIndustry() ? (
                          <>
                            <td>
                              <div className="d-inline-block">
                                <span className="ttl-price-txt">${getFormattedSum(tableData, "OilBasedMud")}</span>
                                <h5 className="title-sml-wts">OIL BASED MUD</h5>
                              </div>
                            </td>
                            <td>
                              <div className="d-inline-block">
                                <span className="ttl-price-txt">${getFormattedSum(tableData, "WaterBasedMud")}</span>
                                <h5 className="title-sml-wts">WATER BASED MUD</h5>
                              </div>
                            </td>
                            <td>
                              <div className="d-inline-block">
                                <span className="ttl-price-txt">${getFormattedSum(tableData, "StandBy")}</span>
                                <h5 className="title-sml-wts">STAND BY</h5>
                              </div>
                            </td>
                            <td>
                              <div className="d-inline-block">
                                <span className="ttl-price-txt">${getFormattedSum(tableData, "RigMove")}</span>
                                <h5 className="title-sml-wts">RIG MOVE</h5>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td>
                              <div className="d-inline-block">
                                <span className="ttl-price-txt">${getFormattedSum(tableData, "QuotedPrice")}</span>
                                <h5 className="title-sml-wts">QUOTED PRICE</h5>
                              </div>
                            </td>
                            <td>
                              <div className="d-inline-block">
                                <span className="ttl-price-txt">${getFormattedSum(tableData, "StandBy")}</span>
                                <h5 className="title-sml-wts">STAND BY</h5>
                              </div>
                            </td>
                          </>
                        )}
                        <td></td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              <div className="p-3 bg-light border-top">
                <div className="row align-items-center">
                  <div className="col-12 col-md-9"></div>
                  <div className="col-12 col-md-3 text-md-end mt-3 mt-md-0">
                    <div className="fw-bold fs-5 mb-2">
                      Grand Total: $
                      {isOFSIndustry() ? (
                        formatCurrency(
                          calSum(tableData, "OilBasedMud") +
                          calSum(tableData, "WaterBasedMud") +
                          calSum(tableData, "StandBy") +
                          calSum(tableData, "RigMove")
                        )
                      ) : (
                        formatCurrency(calSum(tableData, "StandBy") + calSum(tableData, "QuotedPrice"))
                      )}
                    </div>

                    {pricebook_id ? (
                      <button onClick={handleUpdate} className="btn table-submit-btn">Update Price Book</button>
                    ) : (
                      <button onClick={handleSave} className="btn table-submit-btn">Save Price Book</button>
                    )}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </div>

      {/* ---------------- XLS Upload Wizard ---------------- */}
      {showUploadModal && (
        <>
          <style>{`
            .pb-upload-overlay {
              position: fixed; inset: 0; z-index: 9999;
              display: flex; align-items: center; justify-content: center;
              padding: 10px; background: rgba(8, 20, 35, .62);
              backdrop-filter: blur(2px);
            }
            .pb-upload-modal {
              width: min(96vw, 710px);
              max-height: calc(100vh - 20px);
              display: flex; flex-direction: column;
              overflow: hidden; position: relative;
              background: #fff; border: 2px solid #2d8cff;
              border-radius: 16px;
              box-shadow: 0 20px 60px rgba(0,0,0,.30);
            }
            .pb-upload-modal.pb-review-modal { width: min(96vw, 960px); }
            .pb-upload-close {
              position: absolute; top: 10px; right: 12px; z-index: 20;
              width: 34px; height: 34px; border: 0; border-radius: 50%;
              background: #fff; color: #334155; font-size: 25px; line-height: 1;
              cursor: pointer; box-shadow: 0 2px 8px rgba(0,0,0,.12);
            }
            .pb-upload-steps {
              display: flex; align-items: center; justify-content: center;
              gap: 12px; min-height: 68px; padding: 12px 18px;
              background: #f8fafc; border-bottom: 1px solid #dfe6ef; flex-shrink: 0;
            }
            .pb-step { display: flex; align-items: center; gap: 8px; color: #6c7b91; font-size: 15px; }
            .pb-step-number {
              width: 30px; height: 30px; border-radius: 50%;
              display: inline-flex; align-items: center; justify-content: center;
              border: 1px solid #cbd5e1; background: #fff; color: #718096; font-weight: 600;
            }
            .pb-step.active { color: #101b32; font-weight: 700; }
            .pb-step.active .pb-step-number { color: #fff; background: #1461bd; border-color: #1461bd; box-shadow: 0 0 0 4px #dbeafe; }
            .pb-step.done .pb-step-number { color: #fff; background: #1461bd; border-color: #1461bd; }
            .pb-step-line { width: 48px; height: 1px; background: #d5dce6; }
            .pb-step-line.done { background: #1461bd; }
            .pb-upload-body {
              padding: 26px 24px;
              overflow-y: auto; overflow-x: hidden;
              flex: 1;
            }
            .pb-upload-title { margin: 0 0 8px; color: #101b32; font-size: 25px; font-weight: 700; }
            .pb-upload-subtitle { margin: 0; color: #1e1e1e; font-size: 16px; }
            .pb-drop-zone {
              margin-top: 18px; min-height: 270px;
              display: flex; flex-direction: column; align-items: center; justify-content: center;
              border: 1.5px dashed #1570d8; border-radius: 10px;
              background: #f9fbfd; text-align: center; cursor: pointer;
            }
            .pb-upload-icon {
              width: 60px; height: 60px; display: flex; align-items: center; justify-content: center;
              margin-bottom: 12px; border-radius: 50%; background: #edf5ff;
              color: #07559c; font-size: 31px; font-weight: 700;
            }
            .pb-upload-action-title { color: #101b32; font-size: 19px; font-weight: 700; }
            .pb-upload-muted { margin-top: 8px; color: #66758b; font-size: 16px; }
            .pb-upload-hint { margin-top: 10px; color: #a1aec0; font-size: 14px; }
            .pb-primary-btn {
              border: 0; border-radius: 6px; background: #07529a; color: #fff;
              padding: 10px 24px; font-size: 16px; font-weight: 700; cursor: pointer;
            }
            .pb-primary-btn:disabled { background: #d8dde5; color: #aab4c1; cursor: not-allowed; }
            .pb-secondary-btn { border: 0; background: transparent; color: #43536a; padding: 10px 0; font-size: 15px; font-weight: 700; cursor: pointer; }
            .pb-modal-footer {
              min-height: 70px; display: flex; align-items: center; justify-content: space-between;
              gap: 16px; padding: 12px 24px; background: #f8fafc;
              border-top: 1px solid #dfe6ef; flex-shrink: 0;
            }
            .pb-file-card {
              margin-top: 24px; display: flex; align-items: center; justify-content: space-between;
              gap: 18px; padding: 18px 16px; border: 1px solid #277c45;
              border-radius: 8px; background: #f6fcf8;
            }
            .pb-file-info { display: flex; align-items: center; gap: 18px; min-width: 0; }
            .pb-file-badge {
              width: 50px; height: 50px; flex: 0 0 50px;
              display: flex; align-items: center; justify-content: center;
              border-radius: 6px; background: #246b38; color: #fff; font-weight: 800;
            }
            .pb-file-name { color: #172033; font-size: 17px; font-weight: 700; }
            .pb-file-meta { margin-top: 5px; color: #485568; font-size: 14px; }
            .pb-green-btn {
              border: 0; border-radius: 6px; padding: 10px 24px;
              background: #27743d; color: #fff; font-size: 15px; font-weight: 700; cursor: pointer;
            }
            .pb-review-heading { margin-bottom: 14px; }
            .pb-review-title { margin: 0 0 7px; color: #172033; font-size: 17px; font-weight: 700; }
            .pb-review-copy { margin: 0; color: #242424; font-size: 12px; }
            .pb-meta-grid {
              display: grid; grid-template-columns: repeat(6, minmax(0, 1fr));
              border: 1px solid #1461bd; border-radius: 6px;
              background: #f5faff; overflow: hidden;
            }
            .pb-meta-item { min-width: 0; padding: 12px 16px; }
            .pb-meta-label { display: block; margin-bottom: 5px; color: #07529a; font-size: 9px; font-weight: 800; text-transform: uppercase; }
            .pb-meta-value { display: block; color: #12335a; font-size: 12px; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            .pb-review-group {
              margin-top: 12px;
              border: 1px solid #cbd8e7; border-radius: 7px;
              overflow: visible; background: #fff;
              min-height: 90px;
            }
            .pb-review-group-head {
              width: 100%; display: flex; align-items: center; justify-content: space-between;
              padding: 11px 16px; border: 0; background: #f8fafc;
              color: #12335a; cursor: pointer; text-align: left;
              border-top-left-radius: 7px; border-top-right-radius: 7px;
            }
            .pb-review-group-name { font-size: 13px; font-weight: 800; }
            .pb-missing { margin-left: 14px; color: #df2525; font-size: 11px; font-weight: 800; }
            .pb-review-table-wrap {
              overflow-x: auto; overflow-y: visible;
              min-height: 50px;
            }
            .pb-review-table {
              width: 100%; min-width: 1250px;
              border-collapse: collapse; font-size: 11px;
            }
            .pb-review-table th {
              padding: 9px 12px; background: #145ca7; color: #fff;
              font-size: 9px; text-align: left; white-space: nowrap;
            }
            .pb-review-table td {
              padding: 8px 12px; border-bottom: 1px solid #dce4ee;
              background: #fff; vertical-align: middle;
            }
            .pb-review-table tr.pb-invalid-row td { background: #fff8f8; }
            .pb-empty-category-row td {
              padding: 22px 12px; text-align: center; color: #94a3b8;
              font-size: 12px; font-style: italic; background: #fafbfc;
              border-bottom: 1px solid #dce4ee;
            }
            .pb-review-input {
              width: 100%; min-height: 30px; padding: 5px 8px;
              border: 1px solid #cbd5e1; border-radius: 4px;
              background: #fff; color: #172033; font-size: 11px; outline: none;
            }
            .pb-review-input:focus { border-color: #1461bd; box-shadow: 0 0 0 2px rgba(20,97,189,.10); }
            .pb-status { display: inline-flex; align-items: center; justify-content: center; font-size: 15px; }
            .pb-status.valid { color: #2c8a2c; }
            .pb-status.invalid { color: #e22b2b; }
            .pb-add-row {
              display: flex; justify-content: flex-end;
              padding: 7px 10px; border-top: 1px solid #dce4ee; background: #f7fbff;
            }
            .pb-add-row button {
              border: 1px dashed #277bd1; border-radius: 4px;
              padding: 5px 12px; background: #f6fbff; color: #07529a;
              font-weight: 700; cursor: pointer; font-size: 11px;
            }
            .pb-delete-row { border: 0; background: transparent; color: #e22b2b; font-size: 18px; cursor: pointer; }
            .pb-discard { border: 0; background: transparent; color: #43536a; font-weight: 700; cursor: pointer; font-size: 12px; }
            .pb-upload-loading-overlay {
              position: absolute; inset: 0; z-index: 20;
              display: flex; align-items: center; justify-content: center;
              background: rgba(255,255,255,.86);
              backdrop-filter: blur(2px); border-radius: 16px;
            }
            .pb-upload-loader-card {
              min-width: 270px; padding: 24px 28px; text-align: center;
              border: 1px solid #dbe5f0; border-radius: 12px; background: #fff;
              box-shadow: 0 12px 35px rgba(15, 23, 42, .14);
            }
            .pb-upload-spinner {
              width: 42px; height: 42px; margin: 0 auto 14px;
              border: 4px solid #dbeafe; border-top-color: #1461bd;
              border-radius: 50%; animation: pbUploadSpin .8s linear infinite;
            }
            @keyframes pbUploadSpin { to { transform: rotate(360deg); } }
            .pb-template-references {
              margin-top: 14px; padding: 13px 15px;
              border: 1px solid #f1d28a; border-radius: 7px; background: #fffaf0;
            }
            .pb-template-references-title { margin-bottom: 8px; color: #7a5200; font-size: 13px; font-weight: 800; }
            .pb-template-reference {
              display: block; margin-top: 7px; color: #07529a; font-size: 12px;
              line-height: 1.4; word-break: break-all; text-decoration: underline;
            }
            @media (max-width: 760px) {
              .pb-upload-overlay { padding: 4px; }
              .pb-upload-modal, .pb-upload-modal.pb-review-modal { width: 100%; max-height: calc(100vh - 8px); }
              .pb-upload-steps { gap: 6px; }
              .pb-step { font-size: 12px; }
              .pb-step-line { width: 20px; }
              .pb-step-number { width: 26px; height: 26px; }
              .pb-upload-body { padding: 20px 14px; }
              .pb-meta-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
              .pb-file-card { align-items: flex-start; flex-direction: column; }
            }
          `}</style>

          <div className="pb-upload-overlay" role="dialog" aria-modal="true">
            <div className={`pb-upload-modal ${uploadStep === 3 ? "pb-review-modal" : ""}`}>
              <button
                type="button"
                className="pb-upload-close"
                onClick={resetUploadWizard}
                aria-label="Close"
                disabled={uploadSubmitting}
              >
                ×
              </button>

              <div className="pb-upload-steps">
                <div className={`pb-step ${uploadStep >= 1 ? "active" : ""}`}>
                  <span className="pb-step-number">1</span><span>Upload</span>
                </div>
                <span className={`pb-step-line ${uploadStep >= 2 ? "done" : ""}`} />
                <div className={`pb-step ${uploadStep >= 2 ? "active" : ""}`}>
                  <span className="pb-step-number">2</span><span>Review &amp; Edit</span>
                </div>
                <span className={`pb-step-line ${uploadStep >= 3 ? "done" : ""}`} />
                <div className={`pb-step ${uploadStep >= 3 ? "active" : ""}`}>
                  <span className="pb-step-number">3</span><span>Submit</span>
                </div>
              </div>

              {uploadStep === 1 && (
                <>
                  <div className="pb-upload-body">
                    <h2 className="pb-upload-title">Upload Pricebook</h2>
                    <p className="pb-upload-subtitle">Upload your Excel file to import Pricebook details.</p>
                    <div
                      className="pb-drop-zone"
                      onClick={openFilePicker}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const file = e.dataTransfer.files?.[0];
                        if (!file) return;
                        const input = fileInputRef.current;
                        if (input) {
                          const dataTransfer = new DataTransfer();
                          dataTransfer.items.add(file);
                          input.files = dataTransfer.files;
                          input.dispatchEvent(new Event("change", { bubbles: true }));
                        }
                      }}
                    >
                      <div className="pb-upload-icon">↥</div>
                      <div className="pb-upload-action-title">Upload your Document</div>
                      <div className="pb-upload-muted">Drag &amp; drop your file here</div>
                      <div className="pb-upload-hint">Supported formats: .xls, .xlsx (Max 15MB)</div>
                      <button
                        type="button"
                        className="pb-primary-btn"
                        style={{ marginTop: 12 }}
                        onClick={(e) => { e.stopPropagation(); openFilePicker(); }}
                        disabled={uploadingFile}
                      >
                        Choose file
                      </button>
                    </div>
                  </div>
                  <div className="pb-modal-footer">
                    <button type="button" className="pb-secondary-btn" onClick={resetUploadWizard}>Cancel</button>
                    <button type="button" className="pb-primary-btn" disabled>Continue</button>
                  </div>
                </>
              )}

              {uploadStep === 2 && (
                <>
                  <div className="pb-upload-body">
                    <h2 className="pb-upload-title">
                      {uploadValidationError ? "Pricebook Validation" : "Pricebook Uploaded"}
                    </h2>
                    <p className="pb-upload-subtitle">
                      {uploadValidationError
                        ? uploadHasExtractedData
                          ? "The file has validation errors, but extracted data is available. Continue to review and edit it."
                          : "The file could not be validated. Please correct the issue and upload the file again."
                        : "The file was validated successfully. Click Continue to review the extracted data."}
                    </p>
                    <div className="pb-file-card">
                      <div className="pb-file-info">
                        <div className="pb-file-badge">XLS</div>
                        <div style={{ minWidth: 0 }}>
                          <div className="pb-file-name">{uploadedFile?.name || "Pricebook.xlsx"}</div>
                          <div className="pb-file-meta">
                            {uploadedFile
                              ? uploadedFile.size < 1024 * 1024
                                ? `${(uploadedFile.size / 1024).toFixed(1)} KB`
                                : `${(uploadedFile.size / (1024 * 1024)).toFixed(1)} MB`
                              : "Excel file"}{" "}
                            · Validation completed
                          </div>
                        </div>
                      </div>
                      <button type="button" className="pb-green-btn" onClick={openFilePicker} disabled={uploadingFile}>
                        Reupload
                      </button>
                    </div>

                    {uploadValidationError && (
                      <div style={{
                        marginTop: 18, padding: "13px 16px",
                        border: "1px solid #ef4444", borderRadius: 7,
                        background: "#fff5f5", color: "#b42318",
                        fontSize: 14, fontWeight: 600,
                      }}>
                        {uploadValidationError}
                      </div>
                    )}

                    {uploadValidationError && uploadTemplatePaths.length > 0 && (
                      <div className="pb-template-references">
                        <div className="pb-template-references-title">
                          Standard template{uploadTemplatePaths.length > 1 ? "s" : ""}
                        </div>
                        {uploadTemplatePaths.map((templatePath, index) => (
                          <a key={`${templatePath}-${index}`} className="pb-template-reference"
                            href={templatePath} target="_blank" rel="noopener noreferrer">
                            {uploadTemplatePaths.length > 1 ? `Standard template ${index + 1}` : "Standard template"}
                          </a>
                        ))}
                      </div>
                    )}

                    {!uploadValidationError && uploadHasExtractedData && (
                      <div style={{
                        marginTop: 18, padding: "13px 16px",
                        border: "1px solid #2f855a", borderRadius: 7,
                        background: "#f0fff4", color: "#276749",
                        fontSize: 14, fontWeight: 600,
                      }}>
                        ✓ Data extracted successfully. Continue to review and edit the extracted rows.
                      </div>
                    )}
                  </div>
                  <div className="pb-modal-footer">
                    <button type="button" className="pb-secondary-btn" onClick={resetUploadWizard}>Cancel</button>
                    <button type="button" className="pb-primary-btn" onClick={handleUploadContinue}
                      disabled={!uploadHasExtractedData || reviewGroups.length === 0}>
                      Continue
                    </button>
                  </div>
                </>
              )}

              {uploadStep === 3 && (
                <>
                  <div className="pb-upload-body">
                    <div className="pb-review-heading">
                      <h2 className="pb-review-title">Review</h2>
                      <p className="pb-review-copy">
                        Review the details imported from <strong>{uploadedFile?.name}</strong>. Make any required updates and submit to create the Price Book Template.
                      </p>
                    </div>

                    <div className="pb-meta-grid">
                      <div className="pb-meta-item">
                        <span className="pb-meta-label">Customer</span>
                        <span className="pb-meta-value">{getReviewCustomer()}</span>
                      </div>
                      <div className="pb-meta-item">
                        <span className="pb-meta-label">Region</span>
                        <span className="pb-meta-value">
                          {getUploadData()?.Region_name || getUploadData()?.RegionName || selectedRegion?.label || "-"}
                        </span>
                      </div>
                      <div className="pb-meta-item">
                        <span className="pb-meta-label">Country</span>
                        <span className="pb-meta-value">
                          {getUploadData()?.Country || selectedCountry?.label || "-"}
                        </span>
                      </div>
                      <div className="pb-meta-item">
                        <span className="pb-meta-label">Industry</span>
                        <span className="pb-meta-value">
                          {getUploadData()?.IndustryType || selectedIndustry?.label || detectIndustryFromItems() || "-"}
                        </span>
                      </div>
                      <div className="pb-meta-item">
                        <span className="pb-meta-label">Start Date</span>
                        <span className="pb-meta-value">
                          {getUploadData()?.StartDate
                            ? new Date(getUploadData().StartDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                            : startDate || "-"}
                        </span>
                      </div>
                      <div className="pb-meta-item">
                        <span className="pb-meta-label">End Date</span>
                        <span className="pb-meta-value">
                          {getUploadData()?.EndDate
                            ? new Date(getUploadData().EndDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                            : endDate || "-"}
                        </span>
                      </div>
                    </div>

                    {reviewGroups.map((group, groupIndex) => {
                      const invalidCount = (group.details || []).filter((row) => !isReviewRowValid(row)).length;
                      const expanded = reviewExpandedGroups[group.nameField] !== false;
                      const options = getUploadEquipmentsOption(group.nameField);

                      return (
                        <div className="pb-review-group" key={group.id || groupIndex}>
                          <button
                            type="button"
                            className="pb-review-group-head"
                            onClick={() => toggleReviewGroup(group.nameField)}
                          >
                            <span>
                              <span className="pb-review-group-name">{group.nameField}</span>
                              {invalidCount > 0 && (
                                <span className="pb-missing">ⓘ Info Missing: {invalidCount}</span>
                              )}
                            </span>
                            <span>{expanded ? "⌃" : "⌄"}</span>
                          </button>

                          {expanded && (
                            <>
                              <div className="pb-review-table-wrap">
                                <table className="pb-review-table">
                                  <thead>
                                    <tr>
                                      <th>PRODUCT / EQUIPMENT NAME</th>
                                      <th></th>
                                      <th>QTY</th>
                                      {isOFSIndustry() ? (
                                        <>
                                          <th>OIL BASED MUD</th>
                                          <th>WATER BASED MUD</th>
                                          <th>STAND BY</th>
                                          <th>RIG MOVE</th>
                                        </>
                                      ) : (
                                        <>
                                          <th>QUOTED PRICE</th>
                                          <th>STAND BY</th>
                                        </>
                                      )}
                                      <th>ACTION</th>
                                      <th>STATUS</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(group.details || []).length === 0 && (
                                      <tr className="pb-empty-category-row">
                                        <td colSpan={isOFSIndustry() ? 9 : 7}>
                                          No items in this category. Click "+ Add Row" to add one.
                                        </td>
                                      </tr>
                                    )}

                                    {(group.details || []).map((detail, rowIndex) => {
                                      const valid = isReviewRowValid(detail);

                                      const numberInput = (field, min = "0") => (
                                        <input
                                          className="pb-review-input"
                                          type="number"
                                          min={min}
                                          value={detail[field] ?? ""}
                                          onChange={(e) =>
                                            handleReviewRowChange(
                                              groupIndex, rowIndex, field, e.target.value
                                            )
                                          }
                                        />
                                      );

                                      return (
                                        <tr key={detail.id || rowIndex} className={!valid ? "pb-invalid-row" : ""}>
                                          <td>
                                            <PortalDropdown
                                              options={options}
                                              selectedValue={detail.ProductName}
                                              onSelect={(opt) =>
                                                handleReviewProductChange(groupIndex, rowIndex, opt)
                                              }
                                              placeholder="Select..."
                                              isSearchable
                                              isClearable
                                              loading={loadingUploadEquipments}
                                              minMenuWidth={260}
                                              maxMenuHeight={280}
                                            />
                                          </td>

                                          <td>{/* blank */}</td>

                                          <td>{numberInput("Quantity", "0")}</td>

                                          {isOFSIndustry() ? (
                                            <>
                                              <td>{numberInput("OilBasedMud")}</td>
                                              <td>{numberInput("WaterBasedMud")}</td>
                                              <td>{numberInput("StandBy")}</td>
                                              <td>{numberInput("RigMove")}</td>
                                            </>
                                          ) : (
                                            <>
                                              <td>{numberInput("QuotedPrice")}</td>
                                              <td>{numberInput("StandBy")}</td>
                                            </>
                                          )}

                                          <td style={{ textAlign: "center" }}>
                                            {rowIndex > 0 && (
                                              <button
                                                type="button"
                                                className="pb-delete-row"
                                                onClick={() => handleReviewRemoveRow(groupIndex, rowIndex)}
                                                title="Remove row"
                                              >
                                                🗑
                                              </button>
                                            )}
                                          </td>

                                          <td style={{ textAlign: "center" }}>
                                            <span className={`pb-status ${valid ? "valid" : "invalid"}`}
                                              title={valid ? "Valid" : "Information missing"}>
                                              {valid ? "✓" : "!"}
                                            </span>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                              <div className="pb-add-row">
                                <button type="button" onClick={() => handleReviewAddRow(groupIndex)}>
                                  + Add Row
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="pb-modal-footer">
                    <button type="button" className="pb-discard" onClick={openUploadWizard}>
                      Discard &amp; Re Upload
                    </button>
                    <button
                      type="button"
                      className="pb-primary-btn"
                      onClick={handleUploadSubmit}
                      disabled={uploadSubmitting || getReviewCounts().invalid > 0}
                    >
                      {uploadSubmitting ? "Submitting..." : "✓ Submit"}
                    </button>
                  </div>
                </>
              )}

              {uploadingFile && (
                <div className="pb-upload-loading-overlay" aria-live="polite" aria-busy="true">
                  <div className="pb-upload-loader-card">
                    <div className="pb-upload-spinner" />
                    <div className="pb-upload-loading-title">Validating Pricebook...</div>
                    <div className="pb-upload-loading-copy">Uploading and extracting your Excel file. Please wait.</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Home;